import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the using-ai-safely start screen. Three
 * short slides (what/who/why you are the control, main risks, key terms) shown
 * in the reader while the 3D room downloads. Content only; the reader UI lives
 * in ui/StartReader.tsx and the shared art toolkit in ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. assistant panel: work account, a scoped request, a draft to check */
/* ------------------------------------------------------------------ */
function AssistantPanelArt(): JSX.Element {
  const H = 296;
  return (
    <Frame
      label="Example: approved AI assistant"
      aria="An approved AI assistant on a work account, a request that attaches only a few clauses, a draft answer, and a note that you check it before sharing"
      height={H}
    >
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Work assistant
      </text>
      <rect x="252" y="37" width="92" height="24" rx="12" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <circle className="sr-anim-pulse" cx="268" cy="49" r="3.4" fill={C.mint} />
      <text x="277" y="53" fontSize="10.5" fontWeight="700" fill={C.mint} letterSpacing="0.5">
        APPROVED
      </text>
      <line x1="16" y1="72.5" x2="344" y2="72.5" stroke={C.line} />

      <text x="16" y="89" fontSize="10.5" fill={C.muted}>
        Signed in with your own work account
      </text>

      <text x="16" y="110" fontSize="10.5" fill={C.muted}>
        You
      </text>
      <rect x="16" y="116" width="238" height="46" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="29" y="135" fontSize="12.5" fill={C.text}>
        Summarise the obligations and
      </text>
      <text x="29" y="152" fontSize="12.5" fill={C.text}>
        exit terms for Priya
      </text>
      <rect className="sr-anim-blink" x="160" y="141" width="1.5" height="14" rx="0.75" fill={C.mint} />

      <rect x="16.5" y="172.5" width="168" height="22" rx="11" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <rect x="27" y="178" width="9" height="11" rx="1.6" fill="none" stroke={C.mint} strokeWidth="1.4" />
      <text x="43" y="187.5" fontSize="10.5" fontWeight="600" fill={C.mint}>
        Clauses 4 to 7 only
      </text>

      <rect x="16.5" y="204.5" width="327" height="46" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="219" fontSize="10.5" fill={C.muted}>
        Draft from the assistant
      </text>
      <rect x="29" y="227" width="250" height="5" rx="2.5" fill={C.line} />
      <rect x="29" y="237" width="190" height="5" rx="2.5" fill={C.line} />

      <rect x="16.5" y="260.5" width="327" height="28" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="274.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="274.5" r="1.8" fill={C.amber} />
      <text x="46" y="278.5" fontSize="12" fontWeight="600" fill={C.amber}>
        You check it before it is shared
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. data flow: what you paste crosses the line out of your control   */
/* ------------------------------------------------------------------ */
function DataFlowArt(): JSX.Element {
  const H = 298;
  const dot = (cx: number, delay: number) => (
    <circle key={cx} className="sr-anim-blink" cx={cx} cy="124" r="3" fill={C.amber} style={{ animationDelay: `${delay}s` }} />
  );
  return (
    <Frame
      label="What you paste leaves your control"
      aria="A contract with bank details and personal details being sent across a line to an AI service outside your control, with a warning that it cannot be taken back"
      height={H}
    >
      <text x="16" y="44" fontSize="9.5" fontWeight="700" fill={C.mint} letterSpacing="0.8">
        YOUR CONTROL
      </text>
      <text x="236" y="44" fontSize="9.5" fontWeight="700" fill={C.red} letterSpacing="0.8">
        OUTSIDE IT
      </text>

      {/* document */}
      <rect x="16.5" y="54.5" width="136" height="140" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="26" y="70" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        contract.pdf
      </text>
      <line x1="17" y1="77.5" x2="152" y2="77.5" stroke={C.line} />
      <rect x="26" y="90" width="112" height="5" rx="2.5" fill={C.line} />
      <rect x="26" y="112" width="86" height="5" rx="2.5" fill={C.line} />
      <rect className="sr-anim-pulse" x="24" y="128" width="120" height="18" rx="4" fill={C.red} fillOpacity="0.16" stroke={C.red} strokeOpacity="0.5" />
      <text x="31" y="140.5" fontSize="10" fontFamily={MONO} fill={C.red}>
        IBAN CH93 0076 20..
      </text>
      <rect x="24" y="150" width="120" height="18" rx="4" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <text x="31" y="162.5" fontSize="10" fontFamily={MONO} fill={C.amber}>
        Home address, ID
      </text>
      <rect x="24" y="172" width="120" height="18" rx="4" fill={C.red} fillOpacity="0.1" stroke={C.red} strokeOpacity="0.4" />
      <text x="31" y="184.5" fontSize="10" fontFamily={MONO} fill={C.red}>
        Internal notes
      </text>

      {/* arrow with chasing dots */}
      <line x1="158" y1="124" x2="216" y2="124" stroke={C.line} strokeWidth="1.5" />
      {dot(170, 0)}
      {dot(186, 0.35)}
      {dot(202, 0.7)}
      <polyline points="212,118 219,124 212,130" fill="none" stroke={C.amber} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

      {/* boundary */}
      <line x1="226.5" y1="36" x2="226.5" y2="200" stroke={C.red} strokeOpacity="0.6" strokeDasharray="4 4" />

      {/* AI service */}
      <rect x="236.5" y="98.5" width="107" height="52" rx="10" fill={C.panel2} stroke={C.line} />
      <g className="sr-anim-float">
        <circle cx="258" cy="124" r="11" fill={C.mint} fillOpacity="0.12" />
        <rect x="251" y="118" width="14" height="10" rx="2.5" fill="none" stroke={C.mint} strokeWidth="1.5" />
        <line x1="258" y1="114.5" x2="258" y2="118" stroke={C.mint} strokeWidth="1.5" strokeLinecap="round" />
      </g>
      <text x="274" y="121" fontSize="12" fontWeight="700" fill={C.text}>
        AI service
      </text>
      <text x="274" y="136" fontSize="9.5" fill={C.muted}>
        keeps a copy
      </text>
      <text x="290" y="172" fontSize="10.5" fill={C.muted} textAnchor="middle">
        Logs, links, other
      </text>
      <text x="290" y="186" fontSize="10.5" fill={C.muted} textAnchor="middle">
        people on the account
      </text>

      {/* warning */}
      <rect x="16.5" y="210.5" width="327" height="76" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="216" width="3" height="65" rx="1.5" fill={C.red} />
      <g className="sr-anim-pulse">
        <path d="M36 224 l8 14 h-16 z" fill="none" stroke={C.red} strokeWidth="1.6" strokeLinejoin="round" />
        <line x1="36" y1="228.5" x2="36" y2="233" stroke={C.red} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="36" cy="235.6" r="1" fill={C.red} />
      </g>
      <text x="56" y="236" fontSize="12.5" fontWeight="700" fill={C.red}>
        You cannot take it back
      </text>
      <text x="32" y="256" fontSize="11" fill={C.soft}>
        Bank details, personal data and access codes
      </text>
      <text x="32" y="272" fontSize="11" fill={C.soft}>
        stay out of the prompt unless the task needs them.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: six terms as icon chips                                */
/* ------------------------------------------------------------------ */
type Glyph = "tool" | "account" | "prompt" | "sensitive" | "inject" | "link";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  const r = { ...s, stroke: C.red } as const;
  switch (g) {
    case "tool":
      return (
        <g {...s}>
          <path d="M0 -8.5 L7.5 -5.5 V0.5 C7.5 5 4 7.5 0 9 C-4 7.5 -7.5 5 -7.5 0.5 V-5.5 Z" />
          <polyline points="-3.2,0.4 -0.8,3 3.6,-2.4" />
        </g>
      );
    case "account":
      return (
        <g {...s}>
          <circle cx="0" cy="-3.6" r="3.6" />
          <path d="M-7 8 a7 5.4 0 0 1 14 0" />
        </g>
      );
    case "prompt":
      return (
        <g {...s}>
          <rect x="-9" y="-7" width="18" height="14" rx="2.5" />
          <polyline points="-5,-2.5 -1.5,0 -5,2.5" />
          <line className="sr-anim-blink" x1="1" y1="3" x2="5" y2="3" />
        </g>
      );
    case "sensitive":
      return (
        <g {...r}>
          <rect x="-6.5" y="-1.5" width="13" height="9.5" rx="2" />
          <path d="M-3.5 -1.5 V-4.5 a3.5 3.5 0 0 1 7 0 V-1.5" />
          <circle cx="0" cy="3.2" r="1" fill={C.red} />
        </g>
      );
    case "inject":
      return (
        <g {...s}>
          <polyline points="-4.5,-4.5 -9,0 -4.5,4.5" />
          <polyline points="4.5,-4.5 9,0 4.5,4.5" />
          <line x1="0" y1="-5" x2="0" y2="1.4" />
          <circle cx="0" cy="4.6" r="0.9" fill={C.mint} />
        </g>
      );
    case "link":
      return (
        <g {...s}>
          <rect x="-9.5" y="-3.5" width="11" height="7" rx="3.5" />
          <rect x="-1.5" y="-3.5" width="11" height="7" rx="3.5" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  l1: string;
  l2: string;
}

const CHIPS: ChipSpec[] = [
  { g: "tool", title: "Approved tool", l1: "AI the company has vetted", l2: "and can manage." },
  { g: "account", title: "Work account", l1: "Your own sign-in. Not a", l2: "shared or personal login." },
  { g: "prompt", title: "Prompt", l1: "Everything you type or", l2: "attach for the assistant." },
  { g: "sensitive", title: "Sensitive data", l1: "Personal, financial, access", l2: "or confidential details." },
  { g: "inject", title: "Prompt injection", l1: "Hidden text in a file that", l2: "tries to steer the AI." },
  { g: "link", title: "Public share link", l1: "A web address anyone can", l2: "open. No login, no log." },
];

function TermsArt(): JSX.Element {
  const H = 292;
  return (
    <Frame
      label="Terms at a glance"
      aria="Six terms shown as labelled icon chips: approved tool, work account, prompt, sensitive data, prompt injection and public share link"
      height={H}
    >
      {CHIPS.map((c, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = 16 + col * 172;
        const y = 36 + row * 84;
        const warn = c.g === "sensitive";
        const tone = warn ? C.red : C.mint;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width="155" height="75" rx="10" fill={C.panel2} stroke={C.line} />
            <circle cx="26" cy="24" r="16" fill={tone} fillOpacity="0.12" />
            {warn ? <circle className="sr-anim-ring" cx="26" cy="24" r="16" fill="none" stroke={C.red} strokeWidth="1.4" /> : null}
            <g transform="translate(26 24)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="28" fontSize="12.5" fontWeight="700" fill={C.text}>
              {c.title}
            </text>
            <text x="12" y="53" fontSize="10.5" fill={C.muted}>
              {c.l1}
            </text>
            <text x="12" y="66" fontSize="10.5" fill={C.muted}>
              {c.l2}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const USING_AI_SAFELY_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Use AI at work, on purpose.",
    illustration: AssistantPanelArt,
    caption: "The assistant drafts. You decide what goes in and what goes out.",
    sections: [
      {
        h: "What this is about",
        body: "A normal day at the office. A colleague asks you to summarise a contract with an AI assistant. You pick the account, decide what to attach, and choose how the result gets back to her.",
      },
      {
        h: "Who it\u2019s for",
        body: "Anyone who uses an AI tool for work: writing, summarising, analysing or drafting. No technical background needed.",
      },
      {
        h: "Why you are the control",
        body: "The tool cannot tell a public paragraph from a bank account number. It works with whatever it is given. Which tool you use, what goes in and whether the answer is right are your calls.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "What you send is out of your hands.",
    illustration: DataFlowArt,
    caption: "Send the least that answers the request.",
    sections: [
      {
        h: "Wrong tool, wrong account",
        body: "A personal or shared login sits outside company control. The company cannot manage, keep or delete what you sent there.",
      },
      {
        h: "Too much goes in",
        body: "A whole-file upload carries names, bank details and access codes the task never needed. Send only the clauses that answer the question.",
      },
      {
        h: "Public share links",
        body: "Anyone with the link can open the chat and its files. There is no login and no access log. Send results through a normal work channel instead.",
      },
      {
        h: "A fluent answer can be wrong",
        body: "Check a draft against policy before it leaves. Text hidden in a file can also try to steer the assistant. Treat it as untrusted and keep to your own task.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions. Refer back any time.",
    sections: [
      { h: "Approved tool", body: "An AI service the company has vetted and can manage. Use it for company work." },
      { h: "Work account", body: "Your own sign-in to that tool. Not a personal account and not a shared login." },
      { h: "Prompt", body: "Everything you type or attach for the assistant. It all leaves your device." },
      { h: "Sensitive data", body: "Personal, financial, access or confidential details. Leave it out unless the task needs it." },
      { h: "Prompt injection", body: "Hidden instructions in a file or page that try to make the assistant ignore your task." },
      { h: "Public share link", body: "A web address anyone can open. It has no login and no record of who looked." },
    ],
  },
];
